import { Image } from 'antd';
import {
    useGetPatientOrdersQuery,
    useUpdatePatientOrderMutation,
} from '../../features/patient/patientService';
import { Link, useParams } from 'react-router-dom';
import { OrderResponse, OrderStatusEnum } from '../../models/PatientModels';
import { useState } from 'react';
import ConfirmationModal from './ConfirmationModal';
import {
    OrderContainer,
    OrderFooter,
    OrderItem,
    OrderNotFound,
    OrderTitle,
} from './OrderCard';
import { EditOutlined } from '@ant-design/icons';
import { OrderTab, OrderTabGrid } from './OrderTab';

const OrderCard = ({
    order,
    handleUpdate,
}: {
    order: OrderResponse;
    handleUpdate: (orderId: string) => void;
}) => {
    return (
        <OrderContainer
            title={
                <OrderTitle>
                    <div className="text-wrap d-inline-block me-3">
                        <span>{order.drugName}</span>
                    </div>
                    <Link
                        to={`/patient/${order.patientId}/update-medication/${order.id}`}
                    >
                        <EditOutlined
                            rev={undefined}
                            style={{ color: 'rgb(88, 89, 91)' }}
                        />
                    </Link>
                </OrderTitle>
            }
        >
            <OrderItem label="Drug Format" value={order.drugFormat} />
            <OrderItem label="Quantity" value={order.quantity} />
            <OrderItem label="Dosage Direction" value={order.directions} />
            <OrderFooter onClick={() => handleUpdate(order.id)}>
                <Image
                    width="1.5em"
                    preview={false}
                    src="/assets/send_icon.svg"
                    className="cursor-pointer"
                />
            </OrderFooter>
        </OrderContainer>
    );
};

const OrderList = () => {
    const { id: patientId = '' } = useParams();
    const { data: patientOrders, isLoading } = useGetPatientOrdersQuery({
        patientId: patientId,
        orderStatus: OrderStatusEnum.Draft,
    });

    const [updatePatientOrder, { isLoading: isUpdatingOrder }] =
        useUpdatePatientOrderMutation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleUpdate = async (orderId: string) => {
        const response = await updatePatientOrder({
            orderId: orderId,
            patientId: patientId,
        });
        if (typeof response === 'object' && 'data' in response)
            setIsModalOpen(true);
    };

    return (
        <>
            <OrderTab isLoading={isUpdatingOrder || isLoading}>
                <OrderTabGrid>
                    {patientOrders &&
                        patientOrders.length !== 0 &&
                        patientOrders.map((order, index) => (
                            <OrderCard
                                key={index}
                                order={order}
                                handleUpdate={handleUpdate}
                            />
                        ))}
                </OrderTabGrid>
                {!isLoading &&
                    (!patientOrders || patientOrders.length === 0) && (
                        <OrderNotFound>
                            There are no orders to be displayed.
                        </OrderNotFound>
                    )}
            </OrderTab>
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title='Successful!'
                message='The medication(s) were successfully sent to ePrescribe.'
                primaryButtonText='Review Medications'
                secondaryButtonText='View Medication Queue'
                onPrimaryButtonLinkTo={`/patient/${patientId}/approve-medications/view`}
                onSecondaryButtonLinkTo={`/patient/${patientId}/add-new-medication`}
            />
        </>
    );
};

export default OrderList;
